<template>
  <div class="filter-section">
    <!-- 第一行 -->
    <div class="filter-row">
      <div class="filter-item">
        <label>入表时间：</label>
        <div class="input-group">
          <el-date-picker
            v-model="startDate"
            type="month"
            placeholder="开始时间"
            format="yyyy-MM"
            value-format="yyyy-MM"
            :picker-options="{
              disabledDate(time) {
                return time.getTime() > Date.now();
              }
            }"
            @change="onStartDateChange"
            clearable
            style="width: 140px;"
          />
          <span class="to-text">-</span>
          <el-date-picker
            v-model="endDate"
            type="month"
            placeholder="结束时间"
            format="yyyy-MM"
            value-format="yyyy-MM"
            :picker-options="{
              disabledDate(time) {
                return time.getTime() > Date.now();
              }
            }"
            @change="onEndDateChange"
            clearable
            style="width: 140px;"
          />
        </div>
      </div>
      <div class="filter-item">
        <label>省级行政区：</label>
        <el-select 
          v-model="province" 
          placeholder="全部"
          clearable
          @clear="() => { province = ''; }"
        >
          <el-option v-for="option in options.province_area" :key="option" :label="option" :value="option"></el-option>
        </el-select>
      </div>
    </div>

    <!-- 第二行 -->
    <div class="filter-row">
      <div class="filter-item">
        <label>入表企业：</label>
        <el-select @clear="onCompanyCleared"
          v-model="company"
          filterable
          remote
          clearable
          placeholder="请输入关键词"
          :remote-method="fetchCompanyOptions"
          :loading="loading.company"
        >
          <el-option
            v-for="item in companyOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </div>
      <div class="filter-item">
        <label>数据资产内容：</label>
        <el-select @clear="onContentCleared"
          v-model="content"
          filterable
          remote
          clearable
          placeholder="请输入关键词"
          :remote-method="fetchContentOptions"
          :loading="loading.content"
        >
          <el-option
            v-for="item in contentOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </div>
    </div>

    <!-- 第三行 -->
    <div class="filter-row row-actions">
      <div class="action-buttons">
        <button class="confirm-btn" @click="confirmFilters" :disabled="isConfirmLoading">
          <LoadingSpinner v-if="isConfirmLoading" :visible="true" :inline="true" text="筛选中..." />
          <span v-else>确认</span>
        </button>
        <button class="clear-all-btn" @click="clearFilters" :disabled="isClearLoading">
          <LoadingSpinner v-if="isClearLoading" :visible="true" :inline="true" text="清除中..." />
          <span v-else>清除筛选</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';

export default {
  name: 'NLFilterSection',
  components: {
    LoadingSpinner
  },
  props: {
    apiPrefix: {
      type: String,
      required: true
    },
    options: {
      type: Object,
      default: () => ({})
    },
    filters: {
      type: Object,
      default: () => ({})
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isAdminPreview: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      startDate: '',
      endDate: '',
      province: '',
      company: '',
      content: '',
      companyOptions: [],
      contentOptions: [],
      loading: {
        company: false,
        content: false
      },
      // 独立的按钮 loading 状态
      isConfirmLoading: false,
      isClearLoading: false
    };
  },
  watch: {
    filters: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.startDate = newVal.startDate || '';
        this.endDate = newVal.endDate || '';
        this.province = newVal.province || '';
        this.company = newVal.company || '';
        this.content = newVal.content || '';
      }
    }
  },
  methods: {
    fetchCompanyOptions(query) {
      if (!query) return;
      this.loading.company = true;
      const adminParam = this.isAdminPreview ? '&adminView=true' : '';
      axios
        .get(`${this.apiPrefix}/search/company?q=${query}${adminParam}`)
        .then(res => {
          this.companyOptions = (res.data || []).map(item => ({
            label: item,
            value: item
          }));
        })
        .finally(() => {
          this.loading.company = false;
        });
    },
    fetchContentOptions(query) {
      if (!query) return;
      this.loading.content = true;
      const adminParam = this.isAdminPreview ? '&adminView=true' : '';
      axios
        .get(`${this.apiPrefix}/search/content?q=${query}${adminParam}`)
        .then(res => {
          this.contentOptions = (res.data || []).map(item => ({
            label: item,
            value: item
          }));
        })
        .finally(() => {
          this.loading.content = false;
        });
    },
    confirmFilters() {
      // 设置确认按钮 loading 状态
      this.isConfirmLoading = true;
      
      // 时间联动逻辑：确保开始时间不大于结束时间
      if (this.startDate && this.endDate && this.startDate > this.endDate) {
        // 如果开始时间大于结束时间，自动交换
        [this.startDate, this.endDate] = [this.endDate, this.startDate];
      }
      
      const payload = { 
        startDate: this.startDate,
        endDate: this.endDate,
        province: this.province,
        company: this.company,
        content: this.content 
      };

      // 发送筛选事件
      this.$emit('filter-change', payload);
      
      // 延迟重置 loading 状态，确保用户能看到 loading 效果
      setTimeout(() => {
        this.isConfirmLoading = false;
      }, 500);
    },
    clearFilters() {
      // 设置清除按钮 loading 状态
      this.isClearLoading = true;
      
      this.startDate = '';
      this.endDate = '';
      this.province = '';
      this.company = '';
      this.content = '';
      
      // 直接发送事件，不调用 confirmFilters 避免触发确认按钮的 loading
      const payload = { 
        startDate: this.startDate,
        endDate: this.endDate,
        province: this.province,
        company: this.company,
        content: this.content 
      };
      
      this.$emit('filter-change', payload);
      
      // 延迟重置 loading 状态，确保用户能看到 loading 效果
      setTimeout(() => {
        this.isClearLoading = false;
      }, 500);
    },
    onCompanyCleared() {
      this.company = '';
      // 直接发送事件，不调用 confirmFilters
      const payload = { 
        startDate: this.startDate,
        endDate: this.endDate,
        province: this.province,
        company: this.company,
        content: this.content 
      };
      this.$emit('filter-change', payload);
    },
    onContentCleared() {
      this.content = '';
      // 直接发送事件，不调用 confirmFilters
      const payload = { 
        startDate: this.startDate,
        endDate: this.endDate,
        province: this.province,
        company: this.company,
        content: this.content 
      };
      this.$emit('filter-change', payload);
    },
    
    // 比较年月格式 (YYYY-MM)
    onStartDateChange(val) {
      if (val) {
        this.startDate = val;
      } else {
        this.startDate = '';
      }
    },
    onEndDateChange(val) {
      if (val) {
        this.endDate = val;
      } else {
        this.endDate = '';
      }
    }
  }
};
</script>


<style scoped>
  .filter-section {
    background-color: white;
    padding: 20px 30px;
    border-radius: 8px;
    margin: 30px;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .filter-row {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
  }

  .filter-item {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1 1 300px;
  }

  .filter-item label {
    font-weight: bold;
    color: #2e3968;
    white-space: nowrap;
    width: 110px;
    text-align: left;
  }

  select, input {
    padding: 6px 12px;
    border-radius: 6px;
    border: 1px solid #ccc;
    min-width: 160px;
  }

  .to-text {
    margin: 0 5px;
  }

  .row-actions {
    justify-content: flex-end;
  }

  .action-buttons {
    display: flex;
    gap: 30px;
    margin-right: 100px;
  }
  .confirm-btn,
  .clear-all-btn {
    background-color: #2e3968;
    color: white;
    padding: 6px 22px;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-top: 20px;
    min-width: 100px;
  }

  .confirm-btn:hover,
  .clear-all-btn:hover {
    background-color: white;
    color: #2e3968;
  }

  .confirm-btn:disabled,
  .clear-all-btn:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    color: #666;
  }

  .confirm-btn:disabled:hover,
  .clear-all-btn:disabled:hover {
    background-color: #cccccc;
    color: #666;
  }

  @media (max-width: 992px) {
  .filter-section {
    padding: 15px;
    margin: 15px;
    gap: 15px;
  }

  .filter-row {
    flex-direction: column;
    gap: 15px;
    margin-top: 0 !important;
  }

  .filter-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    flex: 0 1 auto; 
  }

  .filter-item label {
    width: auto;
  }

  .filter-item > .el-select,
  .filter-item > input {
    width: 100%;
  }

  .input-group {
    display: flex;
    align-items: center;
    width: 100%;
    gap: 10px;
  }

  .input-group .el-select {
    flex: 1;
  }

  .input-group .to-text {
    flex: 0 0 auto;

  .row-actions {
    justify-content: center;
    margin-top: 0;
  }
  
  .action-buttons {
    width: 100%;
    margin-right: 0;
    flex-direction: column;
    gap: 15px;
  }

  .confirm-btn,
  .clear-all-btn {
    width: 100%;
    margin-top: 0;
    padding: 12px;
    font-size: 15px;
    align-items: center;
  }
}
}

</style>